import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { GoogleSignInButton, AuthDivider } from '../components/GoogleSignInButton';
import { PhoneInput } from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const google = location.state?.google || null;

  const handleGoogleComplete = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/google/register`, { pending_token: google.pending_token, phone });
      setPendingApproval(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (t('register_generic_error') || 'Une erreur est survenue.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('register_password_mismatch'));
      return;
    }

    if (password.length < 6) {
      setError('Min. 6 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await register(phone, password, name, email);
      if (result.status === 'pending') {
        setPendingApproval(true);
      } else {
        navigate('/compte');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || String(e)).join('. '));
      } else if (!error.response) {
        setError(t('register_network_error') || 'Erreur de connexion. Vérifiez votre réseau et réessayez.');
      } else {
        setError(t('register_generic_error') || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[color:var(--color-bg)] flex items-center justify-center px-4 py-24"
    >
      <div className="absolute inset-0 bg-sand overflow-hidden pointer-events-none"><div className="absolute -left-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-[color:var(--color-primary)] opacity-10" /><div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-[color:var(--color-emerald)] opacity-10" /></div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-[color:rgb(var(--surface-rgb)/0.92)] backdrop-blur-xl border border-white/10 p-8 rounded-3xl shadow-xl">
          <div className="text-center mb-8">
            <Link to="/">
              <span className="text-3xl font-bold tracking-tight text-[color:var(--color-primary)]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span style={{ color: 'var(--color-logo-gold)' }}>Ô'</span>Delicieuse <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[color:var(--color-emerald)] align-middle ml-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Wholesale</span>
              </span>
            </Link>
          </div>

          {pendingApproval ? (
            <div className="text-center py-6" data-testid="pending-approval-message">
              <CheckCircle className="w-16 h-16 text-[color:var(--color-primary)] mx-auto mb-4" />
              <h2 className="text-2xl text-white font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('register_received')}
              </h2>
              <p className="text-zinc-400 mb-6">{t('register_pending_msg')}</p>
              <Link to="/connexion" className="text-[color:var(--color-primary)] hover:underline">{t('register_back_login')}</Link>
            </div>
          ) : google ? (
            <form onSubmit={handleGoogleComplete} className="space-y-6" data-testid="google-complete-form">
              <h1 className="text-3xl text-white font-bold text-center mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('google_phone_title')}
              </h1>
              <p className="text-zinc-400 text-sm text-center" data-testid="google-complete-email">{google.name ? `${google.name} · ` : ''}{google.email}</p>
              <p className="text-zinc-400 text-sm text-center">{t('google_phone_msg')}</p>
              <div>
                <label className="input-label">{t('register_phone')}</label>
                <PhoneInput value={phone} onChange={setPhone} testId="google-complete-phone" autoFocus />
              </div>
              {error && <p className="text-red-500 text-sm text-center" data-testid="register-error">{error}</p>}
              <button type="submit" disabled={loading} className="w-full btn-primary disabled:opacity-50" data-testid="google-complete-submit">
                {loading ? t('register_loading') : t('register_submit')}
              </button>
              <p className="text-center">
                <Link to="/connexion" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-sm" data-testid="google-complete-cancel">{t('register_back_login')}</Link>
              </p>
            </form>
          ) : (
            <>
              <h1 className="text-3xl text-white font-bold text-center mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('register_title')}
              </h1>

              <GoogleSignInButton />
              <AuthDivider />

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="input-label">{t('register_name')}</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="w-full input-field" placeholder="" data-testid="register-name" />
                </div>

                <div>
                  <label className="input-label">{t('register_phone')}</label>
                  <PhoneInput value={phone} onChange={setPhone} testId="register-phone" />
                </div>

                <div>
                  <label className="input-label">{t('register_email_label')}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full input-field" placeholder="email@example.com" data-testid="register-email" />
                  <p className="text-zinc-600 text-xs mt-1">{t('register_email_required')}</p>
                </div>

                <div>
                  <label className="input-label">{t('register_password')}</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full input-field pr-12" placeholder="••••••••" data-testid="register-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="input-label">{t('register_confirm_password')}</label>
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full input-field" placeholder="••••••••" data-testid="register-confirm-password" />
                </div>

                {error && <p className="text-red-500 text-sm text-center" data-testid="register-error">{error}</p>}

                <button type="submit" disabled={loading} className="w-full btn-primary disabled:opacity-50" data-testid="register-submit">
                  {loading ? t('register_loading') : t('register_submit')}
                </button>
              </form>

              <p className="text-center text-zinc-500 text-sm mt-6">
                {t('register_has_account')}{' '}
                <Link to="/connexion" className="text-[color:var(--color-primary)] hover:underline">{t('register_login')}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
